import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Check, ChevronDown, Pencil, Trash2, Building2, Mail, Phone, Loader2, Globe } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useEntity, createItem, updateItem, deleteItem } from "../hooks/useEntity";
import api, { apiError } from "../lib/api";
import { formatPhone, formatPhoneInput } from "../lib/utils-format";
import { DIRECTORY_TYPES, DIRECTORY_TRADES, DIRECTORY_STATUSES } from "../config/homebuilding";

const dirName = (r) =>
  [r.first_name, r.last_name].filter(Boolean).join(" ") || r.company_name || "—";

function ProjectPicker({ projects, value = [], onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 text-sm text-left" data-testid="dir-project-picker">
          <span className={selected.length ? "" : "text-muted-foreground"}>
            {selected.length ? `${selected.length} project${selected.length > 1 ? "s" : ""} linked` : "Link to projects…"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-1" align="start">
        <div className="max-h-64 overflow-y-auto slim-scroll">
          {projects.length === 0 && <div className="px-2 py-3 text-sm text-muted-foreground">No projects yet</div>}
          {projects.map((p) => {
            const on = selected.includes(p.id);
            return (
              <button key={p.id} type="button" onClick={() => toggle(p.id)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-left"
                data-testid={`dir-project-opt-${p.id}`}>
                <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? "bg-primary border-primary text-primary-foreground" : "border-input"}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span className="truncate">{p.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DirField({ form, set, k, label, type = "text", placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input type={type} placeholder={placeholder} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`dir-field-${k}`} />
    </div>
  );
}

function PhoneField({ form, set, k, label }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input value={form[k] ?? ""} onChange={(e) => set(k, formatPhoneInput(e.target.value))} placeholder="(XXX)XXX-XXXX" data-testid={`dir-field-${k}`} />
    </div>
  );
}

function DirectoryForm({ open, onOpenChange, initial, projects, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  useEffect(() => { if (open) setForm(initial || { type: "Subcontractor", status: "Active", project_ids: [] }); }, [open, initial]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onZipChange = async (zip) => {
    set("postal_code", zip);
    const clean = String(zip).replace(/\D/g, "");
    if (clean.length !== 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${clean}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) setForm((f) => ({ ...f, postal_code: clean, city: place["place name"], state: place["state abbreviation"] }));
      }
    } catch { /* zip lookup is best-effort */ } finally { setZipLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name && !form.first_name && !form.last_name) {
      toast.error("Enter a company or a contact name"); return;
    }
    if (!form.type) { toast.error("Select a type"); return; }
    setSaving(true);
    try {
      const names = (form.project_ids || []).map((id) => projects.find((p) => p.id === id)?.label).filter(Boolean);
      const website = (form.website || "").trim();
      const normalizedWebsite = website && !/^https?:\/\//i.test(website) ? `https://${website}` : website;
      const payload = { ...form, website: normalizedWebsite, project_names: names };
      if (initial?.id) { await updateItem("directory", initial.id, payload); toast.success("Entry updated"); }
      else { await createItem("directory", payload); toast.success("Entry added"); }
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto slim-scroll">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{initial?.id ? "Edit Partner Entry" : "New Partner Entry"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Type *</Label>
              <Select value={form.type || ""} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="dir-field-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{DIRECTORY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Trade / Specialty</Label>
              <Select value={form.trade || ""} onValueChange={(v) => set("trade", v)}>
                <SelectTrigger data-testid="dir-field-trade"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{DIRECTORY_TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3"><DirField form={form} set={set} k="company_name" label="Company" /><DirField form={form} set={set} k="website" label="Company Website" type="text" placeholder="e.g. ledgertc.com" /></div>
          <div className="grid grid-cols-2 gap-3"><DirField form={form} set={set} k="email" label="Email" /><PhoneField form={form} set={set} k="business_phone" label="Business Phone" /></div>
          <DirField form={form} set={set} k="address" label="Address" />
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Zip Code</Label>
              <div className="relative">
                <Input value={form.postal_code ?? ""} onChange={(e) => onZipChange(e.target.value)} placeholder="e.g. 83702" className="pr-8" data-testid="dir-field-postal_code" />
                {zipLoading && <Loader2 className="h-4 w-4 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />}
              </div>
            </div>
            <DirField form={form} set={set} k="city" label="City" />
            <DirField form={form} set={set} k="state" label="State" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="dir-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Directory() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [tradeFilter, setTradeFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [projects, setProjects] = useState([]);

  const params = { search, limit: 500 };
  if (typeFilter !== "all") params.type = typeFilter;
  const { items, total, loading, reload } = useEntity("directory", params);

  useEffect(() => {
    api.get("/projects?limit=500").then((r) => {
      setProjects((r.data.items || []).map((p) => ({
        id: p.id,
        label: p.project_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.company_name || "Project",
      })));
    }).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => items.filter((r) => tradeFilter === "all" || r.trade === tradeFilter),
    [items, tradeFilter]
  );

  const columns = [
    { key: "name", label: "Name / Company", render: (r) => (
        <div><div className="font-medium">{dirName(r)}</div>{r.company_name && dirName(r) !== r.company_name && <div className="text-xs text-muted-foreground">{r.company_name}</div>}</div>
      ) },
    { key: "type", label: "Type", badge: true },
    { key: "trade", label: "Trade", render: (r) => r.trade || "—" },
    { key: "mobile_phone", label: "Phone", render: (r) => formatPhone(r.mobile_phone || r.business_phone) || "—" },
    { key: "email", label: "Email", render: (r) => r.email || "—" },
    { key: "website", label: "Website", render: (r) => {
        if (!r.website) return "—";
        const href = /^https?:\/\//i.test(r.website) ? r.website : `https://${r.website}`;
        const label = r.website.replace(/^https?:\/\//i, "").replace(/\/$/, "");
        return (
          <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline break-all" data-testid={`dir-website-${r.id}`}>
            <Globe className="h-3.5 w-3.5 shrink-0" />{label}
          </a>
        );
      } },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => { setEditing(r); setFormOpen(true); }} className="p-1.5 rounded hover:bg-accent" data-testid={`dir-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={() => setToDelete(r)} className="p-1.5 rounded hover:bg-accent text-destructive" data-testid={`dir-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ) },
  ];

  const confirmDelete = async () => {
    try { await deleteItem("directory", toDelete.id); toast.success("Entry deleted"); setToDelete(null); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        title="Partners" count={total}
        subtitle="Companies you work with — clients, vendors, subcontractors, and consultants"
        actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="dir-new-btn"><Plus className="h-4 w-4 mr-1.5" />New Partner</Button>}
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search directory…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="dir-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]" data-testid="dir-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {DIRECTORY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tradeFilter} onValueChange={setTradeFilter}>
          <SelectTrigger className="w-[200px]" data-testid="dir-trade-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Trades</SelectItem>
            {DIRECTORY_TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} items={filtered} loading={loading} onRowClick={(r) => navigate(`/directory/${r.id}`)} emptyLabel="No directory entries yet" />

      <DirectoryForm open={formOpen} onOpenChange={setFormOpen} initial={editing} projects={projects} onSaved={reload} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete directory entry?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes {toDelete ? dirName(toDelete) : "this entry"} from the directory.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="dir-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
